import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Grid, Link, MenuItem, Paper,
  Stack, TextField, Typography, useTheme,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HubIcon from '@mui/icons-material/Hub';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SecurityIcon from '@mui/icons-material/Security';
import ForumIcon from '@mui/icons-material/Forum';
import ExtensionIcon from '@mui/icons-material/Extension';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import PublicHeader from '../components/Public/PublicHeader';
import PublicFooter from '../components/Public/PublicFooter';
import Screenshot from '../components/Public/Screenshot';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import {
  CONTACT_EMAIL, HoneypotField, PARTNER_MAILTO, fieldErrorsFrom, requesterFromUser, submitErrorMessage,
} from '../components/Support/supportUi';

const ORG_TYPES = ['MSSP', 'In-house SOC', 'MDR provider', 'Consultancy', 'Other'];
const TEAM_SIZES = ['1-5', '6-20', '21-50', '50+'];
const TOOL_SUGGESTIONS = [
  'Microsoft Defender', 'CrowdStrike Falcon', 'SentinelOne', 'Cortex XDR', 'Splunk', 'Microsoft Sentinel', 'Elastic',
  'QRadar', 'Proofpoint', 'Mimecast', 'Okta', 'AWS GuardDuty', 'Jira', 'ServiceNow',
];

const benefits = [
  { icon: <RocketLaunchIcon />, title: 'Early access & roadmap influence', text: 'Use new capabilities first and help decide what we build next — your priorities go straight onto the roadmap.' },
  { icon: <HubIcon />, title: 'Integrations for your stack', text: 'We build and harden connectors for the EDR, SIEM, email and ticketing tools you already run.' },
  { icon: <SupportAgentIcon />, title: 'Founder-level support & partner pricing', text: 'A direct line to the people building Excubia, plus pricing that reflects the partnership.' },
];

const lookingFor = [
  { icon: <SecurityIcon />, title: 'A SOC or MSSP handling alerts daily', text: 'Teams triaging real alert volume, so what we build is shaped by real workloads.' },
  { icon: <ForumIcon />, title: 'Candid feedback on a regular cadence', text: 'A short call every couple of weeks and honest opinions about what works and what does not.' },
  { icon: <ExtensionIcon />, title: 'One or two tools to integrate first', text: 'Tell us which products matter most and we will start there.' },
];

const InfoCard = ({ icon, title, text, color }) => (
  <Card sx={{ height: '100%' }} variant="outlined">
    <CardContent>
      <Avatar sx={{ bgcolor: color, mb: 2 }}>{icon}</Avatar>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </CardContent>
  </Card>
);

const emptyForm = { name: '', email: '', company: '', phone: '', organizationType: '', teamSize: '', tools: [], message: '', website: '' };

const PartnerPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [toolInput, setToolInput] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [sentTo, setSentTo] = useState('');

  useEffect(() => {
    if (!user) return;
    const who = requesterFromUser(user);
    setForm((f) => ({ ...f, name: f.name || who.name, email: who.email || f.email, company: f.company || who.company }));
  }, [user]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((fe) => ({ ...fe, [key]: undefined }));
  };

  const scrollToForm = () => document.getElementById('partner-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    const local = {};
    if (!form.name.trim()) local.name = 'Name is required';
    if (!form.email.trim()) local.email = 'A valid email is required';
    if (!form.company.trim()) local.company = 'Company is required';
    if (form.message.trim().length < 10) local.message = 'Tell us a little about your team (at least 10 characters)';
    if (Object.keys(local).length) { setFieldErrors(local); return; }

    // Include anything typed but not yet turned into a chip.
    const tools = [...form.tools, ...(toolInput.trim() ? [toolInput.trim()] : [])];
    const details = {};
    if (form.organizationType) details.organizationType = form.organizationType;
    if (form.teamSize) details.teamSize = form.teamSize;
    if (tools.length) details.tools = tools;

    try {
      setSubmitting(true);
      const payload = {
        kind: 'partnership',
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        subject: `Design partner enquiry — ${form.company.trim()}`,
        message: form.message.trim(),
        details,
        website: form.website,
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      const res = await apiService.support.submit(payload);
      setSentTo(payload.email);
      setTicket(res.data.ticket);
    } catch (err) {
      setFieldErrors(fieldErrorsFrom(err));
      setError(submitErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    const who = requesterFromUser(user);
    setForm({ ...emptyForm, name: who.name, email: who.email, company: who.company });
    setToolInput('');
    setTicket(null);
    setError(null);
    setFieldErrors({});
  };

  const heroBg = theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #0b1220 0%, #1a2540 100%)'
    : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <PublicHeader />

      {/* Hero */}
      <Box sx={{ background: heroBg, color: 'common.white', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Chip icon={<HandshakeIcon />} label="Design partner programme" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'common.white', mb: 2, '& .MuiChip-icon': { color: 'common.white' } }} />
          <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.25rem', md: '3.25rem' }, mb: 2 }}>
            Partner with Excubia
          </Typography>
          <Typography variant="h6" component="p" sx={{ fontWeight: 400, opacity: 0.92, mb: 4, maxWidth: 680 }}>
            We are looking for a security team or MSSP to build integrations and shape the product together. You bring the
            day-to-day reality of running a SOC; we bring an engineering team that will build around it.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" color="secondary" size="large" onClick={scrollToForm}>Apply to partner</Button>
            <Button
              variant="outlined" size="large" href={PARTNER_MAILTO} startIcon={<EmailIcon />}
              sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'common.white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Email us
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>What you get</Typography>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3 }}>A product built around your SOC</Typography>
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {benefits.map((b) => (
            <Grid item xs={12} md={4} key={b.title}><InfoCard {...b} color="primary.main" /></Grid>
          ))}
        </Grid>

        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center" sx={{ mb: 7 }}>
          <Grid item xs={12} md={5}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1.5 }}>What you&apos;d be shaping</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Excubia already ingests alerts from five EDRs and any other source, triages them with AI, correlates them into
              cases with an attack timeline, and tracks every case against an SLA. As a design partner you decide what comes next
              and which of your tools we connect first.
            </Typography>
            <Button component={RouterLink} to="/product" endIcon={<ArrowForwardIcon />}>Take the product tour</Button>
          </Grid>
          <Grid item xs={12} md={7}>
            <Screenshot
              src="/screens/dashboard.webp"
              alt="Excubia SOC dashboard with KPI tiles for open detections, open cases, SLA breaches, automation and integrations, plus a needs-attention list and recent critical detections"
              caption="The Excubia SOC dashboard, from a tenant fed by simulated EDR telemetry for a fictional organisation."
              showCaption
            />
          </Grid>
        </Grid>

        <Typography variant="overline" color="secondary" sx={{ fontWeight: 700 }}>What we&apos;re looking for</Typography>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3 }}>Is this a fit?</Typography>
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {lookingFor.map((b) => (
            <Grid item xs={12} md={4} key={b.title}><InfoCard {...b} color="secondary.main" /></Grid>
          ))}
        </Grid>

        <Grid container spacing={4} id="partner-form" sx={{ scrollMarginTop: 80 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 2 }}>Get in touch</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Tell us about your team and the tools you run. We read every submission and reply personally, usually within one business day.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderColor: 'primary.main', borderWidth: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <EmailIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Prefer email?</Typography>
              </Stack>
              <Link href={PARTNER_MAILTO} variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{CONTACT_EMAIL}</Link>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Write to us directly — subject &ldquo;Partnering with Excubia&rdquo;.</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {ticket ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>Thanks — we&apos;ll be in touch</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>Reference <Box component="span" sx={{ fontFamily: 'monospace' }}>{ticket.number}</Box></Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    We&apos;ve emailed a confirmation to <strong>{sentTo}</strong>.
                  </Typography>
                  <Button variant="contained" onClick={reset} sx={{ mt: 3 }}>Submit another</Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent component="form" noValidate onSubmit={submit} sx={{ position: 'relative' }}>
                  {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="Name" value={form.name} onChange={set('name')} error={Boolean(fieldErrors.name)} helperText={fieldErrors.name} autoComplete="name" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth required type="email" label="Work email" value={form.email} onChange={set('email')}
                        error={Boolean(fieldErrors.email)} helperText={fieldErrors.email} InputProps={{ readOnly: Boolean(user) }} autoComplete="email"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth required label="Company" value={form.company} onChange={set('company')} error={Boolean(fieldErrors.company)} helperText={fieldErrors.company} autoComplete="organization" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone (optional)" value={form.phone} onChange={set('phone')} error={Boolean(fieldErrors.phone)} helperText={fieldErrors.phone} autoComplete="tel" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Organization type" value={form.organizationType} onChange={set('organizationType')}>
                        {ORG_TYPES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label="Team size" value={form.teamSize} onChange={set('teamSize')}>
                        {TEAM_SIZES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple freeSolo options={TOOL_SUGGESTIONS} value={form.tools}
                        onChange={(e, value) => setForm((f) => ({ ...f, tools: value.map((v) => String(v).trim()).filter(Boolean).slice(0, 10) }))}
                        inputValue={toolInput} onInputChange={(e, value) => setToolInput(value)}
                        renderTags={(value, getTagProps) => value.map((option, index) => (
                          <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                        ))}
                        renderInput={(params) => (
                          <TextField {...params} label="Tools you'd like integrated" placeholder="Type a tool and press Enter" helperText="EDR, SIEM, email security, ticketing…" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth required multiline minRows={5} label="Message" value={form.message} onChange={set('message')}
                        error={Boolean(fieldErrors.message)} helperText={fieldErrors.message || 'What does your alert workflow look like today, and what would you change?'}
                      />
                    </Grid>
                  </Grid>
                  <HoneypotField value={form.website} onChange={(v) => setForm((f) => ({ ...f, website: v }))} />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" size="large" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}>
                      Send
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
      <PublicFooter />
    </Box>
  );
};

export default PartnerPage;
